import Observer from '@researchgate/react-intersection-observer';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';
import { ActivityNotificationsStore, setGlobalActivityNotificationsStore } from '@udemy/activity-notifications';
import { GoogleOneTap } from '@udemy/auth';
import { Tracker } from '@udemy/event-tracking';
import { useMatchMedia } from '@udemy/hooks';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { LabsLearningBanner } from '@udemy/labs';
import { LanguageSeoLinks, ModalLanguageSelector } from '@udemy/language-selector';
import { Button, IconButton } from '@udemy/react-core-components';
import { AIAssistantEntryPointSelected, LearningAssistantChatTypeOptions, LearningAssistantEntryPointUIRegionOptions } from '@udemy/react-learning-assistant-entry-points';
import { SkipToContentButton } from '@udemy/react-navigation-components';
import { SearchFormAutocomplete, SearchFormAutocompleteStore } from '@udemy/search-form-autocomplete';
import { SmartBarFixedHeight, SmartBarFixedHeightSpacer } from '@udemy/smart-bar';
import { tokens } from '@udemy/styles';
import { useUDData } from '@udemy/ud-data';
import { LanguageSelectorLocation } from '../constants';
import { HeaderContextProvider } from '../contexts/header-context';
import { LoginSelected, SignupSelected, SearchBarMXEnum, SearchBarSelected } from '../events';
import { isAiAssistantEntryPointEnabled, showCobrandingLogo } from '../external/header-experiment-features';
import { loadCommonAppContext } from '../external/load-common-app-context';
import { HeaderStore } from '../header.mobx-store';
import { useHeaderExperimentFeatures } from '../hooks/use-header-experiment-features';
import { useHeaderStore } from '../hooks/use-header-store';
import { usePlansAndPricing } from '../hooks/use-plans-and-pricing';
import { useUfbContext } from '../hooks/use-ufb-context';
import { Logo } from '../logo.react-component';
import { SeoLinks } from '../seo-links/seo-links.react-component';
import { getUserTypes } from '../utils/get-user-types';
import { BrowseNavDropdown } from './browse/browse-nav-dropdown.react-component';
import { BrowseNavStore } from './browse/browse-nav.mobx-store';
import styles from "./desktop-header.module.css";
import twoRowSearchStyles from "./desktop-two-row-header.module.css";
import { HeaderAuthButton } from './header-auth-button.react-component';
import { InstructorDropdown } from './instructor/instructor-dropdown.react-component';
import { MyLearningDropdown } from './my-learning/my-learning-dropdown.react-component';
import { MyLearningStore } from './my-learning/my-learning.mobx-store';
import { NotificationDropdown } from './notification/notification-dropdown.react-component';
import { CartDropdown } from './shopping/cart-dropdown.react-component';
import { WishlistDropdown } from './shopping/wishlist-dropdown.react-component';
import { SubscribeButton } from './subscribe/subscribe-button.react-component';
import { TryUFBDropdown } from './try-ufb/try-ufb-dropdown.react-component';
import { UserProfileDropdown } from './user-profile/user-profile-dropdown.react-component';

/**
 * Module-scoped component for logged-in user header section.
 * Hoisted to prevent remounting when parent re-renders (preserves dropdown state).
 */
const LoggedInPart = observer(({
  useLangPrefixedUrls,
  showSplitCategoriesNav
}) => {
  const headerStore = useHeaderStore();
  const ufbContext = useUfbContext();
  const headerExperimentFeatures = useHeaderExperimentFeatures();
  const udData = useUDData();
  const i18n = useI18n();
  const {
    organizationState
  } = headerStore.userSpecificContext;
  const myLearningGroup = organizationState !== null && organizationState !== void 0 && organizationState.should_show_manage_menu ? 'group-b' : '';
  const shouldShowAiAssistantEntryPoint = isAiAssistantEntryPointEnabled({
    headerExperimentFeatures,
    headerStore
  });
  const handleAiAssistantRedirect = () => {
    Tracker.publishEvent(new AIAssistantEntryPointSelected({
      chatType: LearningAssistantChatTypeOptions.DISCOVERY_CHAT,
      uiRegion: LearningAssistantEntryPointUIRegionOptions.HEADER
    }));
    window.location.href = '/ai-assistant/';
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, !headerStore.isPersonalPlanSubscriber && /*#__PURE__*/React.createElement(TryUFBDropdown, {
    className: classNames(styles['gap-button'], styles['group-a'], {
      [styles['show-on-large-screen']]: showSplitCategoriesNav
    })
  }), ufbContext === null || ufbContext === void 0 ? void 0 : ufbContext.packageAlert, shouldShowAiAssistantEntryPoint && /*#__PURE__*/React.createElement(Button, {
    size: "medium",
    udStyle: "ghost",
    onClick: handleAiAssistantRedirect
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "sparkles",
    size: 'xsmall',
    label: false,
    className: styles['sparkles-icon']
  }), /*#__PURE__*/React.createElement("span", {
    className: classNames('ud-heading-sm', styles['ai-assistant-text'])
  }, i18n.gettext('AI Assistant'))), udData.Config.brand.is_teaching_enabled && headerStore.showInstructorDropdown && /*#__PURE__*/React.createElement(InstructorDropdown, {
    className: classNames(styles['gap-button'], styles['group-b'], {
      [styles['show-on-extra-large-screen']]: showSplitCategoriesNav
    })
  }), ufbContext === null || ufbContext === void 0 ? void 0 : ufbContext.manageElement, /*#__PURE__*/React.createElement(MyLearningDropdown, {
    className: classNames(styles['gap-button'], styles[myLearningGroup])
  }), !headerStore.isPersonalPlanSubscriber && udData.Config.features.wishlist && /*#__PURE__*/React.createElement(WishlistDropdown, {
    className: styles['group-c']
  }), udData.Config.features.shopping_cart && headerStore.showCartDropdown && /*#__PURE__*/React.createElement(CartDropdown, {
    className: ""
  }), udData.Config.features.notifications && /*#__PURE__*/React.createElement(NotificationDropdown, {
    className: styles['group-c']
  }), /*#__PURE__*/React.createElement(UserProfileDropdown, {
    className: "",
    useLangPrefixedUrls: useLangPrefixedUrls
  }));
});

/**
 * Module-scoped component for logged-out user header section.
 * Hoisted to prevent remounting when parent re-renders (preserves dropdown state).
 */
const LoggedOutPart = observer(({
  useLangPrefixedUrls,
  showSplitCategoriesNav
}) => {
  const headerStore = useHeaderStore();
  const ufbContext = useUfbContext();
  const udData = useUDData();
  const i18n = useI18n();
  const {
    showPlansAndPricingFeature
  } = usePlansAndPricing();
  const isUFB = !!ufbContext;
  const showOneTapComponent = udData.Config.brand.is_external_sources_enabled && !isUFB && !/^\/(personal-plan|cart|payment\/checkout)\//.test(window.location.pathname) && !!udData.Config.third_party.google_client_id;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TryUFBDropdown, {
    className: classNames(styles['gap-button'], {
      [styles['group-c-logged-out-variant']]: showPlansAndPricingFeature,
      [styles['group-a']]: !showPlansAndPricingFeature,
      [styles['show-on-large-screen']]: showSplitCategoriesNav
    })
  }), udData.Config.brand.is_teaching_enabled && /*#__PURE__*/React.createElement(InstructorDropdown, {
    className: classNames(styles['gap-button'], {
      [styles['group-a-logged-out-variant']]: showPlansAndPricingFeature,
      [styles['group-b']]: !showPlansAndPricingFeature,
      [styles['show-on-extra-large-screen']]: showSplitCategoriesNav
    })
  }), udData.Config.features.shopping_cart && headerStore.showCartDropdown && /*#__PURE__*/React.createElement(CartDropdown, {
    className: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['gap-auth-button']
  }, /*#__PURE__*/React.createElement(HeaderAuthButton, {
    udStyle: "secondary",
    authParams: {
      showLogin: true
    },
    "data-purpose": "header-login",
    onClick: () => Tracker.publishEvent(new LoginSelected())
  }, i18n.gettext('Log in'))), /*#__PURE__*/React.createElement("div", {
    className: styles['gap-auth-button']
  }, /*#__PURE__*/React.createElement(HeaderAuthButton, {
    "data-purpose": "header-signup",
    authParams: headerStore.signupParams,
    onClick: () => Tracker.publishEvent(new SignupSelected())
  }, i18n.gettext('Sign up'))), /*#__PURE__*/React.createElement("div", {
    className: styles['gap-auth-button']
  }, /*#__PURE__*/React.createElement(ModalLanguageSelector, {
    uiRegion: LanguageSelectorLocation.DESKTOP_HEADER,
    useLangPrefixedUrls: useLangPrefixedUrls,
    trigger: /*#__PURE__*/React.createElement(IconButton, {
      udStyle: "secondary",
      size: "medium"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "language",
      color: "neutral",
      label: i18n.gettext('Choose a language')
    }))
  }), /*#__PURE__*/React.createElement(LanguageSeoLinks, null)), showOneTapComponent && /*#__PURE__*/React.createElement(GoogleOneTap, {
    googleClientId: udData.Config.third_party.google_client_id
  }));
});
export const DesktopHeader = observer(({
  searchPhrase,
  persistentSearch,
  shoppingClient,
  useLangPrefixedUrls = false,
  isAdminOrGroupAdmin = false,
  showSmartBar = true,
  _mockStores,
  showTwoRowSearchBarMX,
  ...headerData
}) => {
  var _useMatchMedia, _udData$Config, _udData$Config2, _headerStore$userSpec2, _headerData$ufbContex5;
  const isUFB = !!headerData.ufbContext;
  const i18n = useI18n();
  const locale = i18n.locale;
  const udData = useUDData();
  const [headerStore] = React.useState(() => {
    var _mockStores$headerSto;
    return (_mockStores$headerSto = _mockStores === null || _mockStores === void 0 ? void 0 : _mockStores.headerStore) !== null && _mockStores$headerSto !== void 0 ? _mockStores$headerSto : new HeaderStore({
      ...headerData,
      shoppingClient
    });
  });
  const [browseNavStore] = React.useState(() => new BrowseNavStore(i18n.gettext, i18n.locale, i18n.lang, udData.request.navigation_locale, isUFB));
  const [searchFormAutocompleteStore] = React.useState(() => {
    const store = new SearchFormAutocompleteStore({
      getFormParams: () => headerStore.formActionParams,
      gettext: i18n.gettext
    });
    store.setInputValue(searchPhrase !== null && searchPhrase !== void 0 ? searchPhrase : '');
    return store;
  });
  const [myLearningStore] = React.useState(() => new MyLearningStore(headerStore));
  const [activityNotificationsStore] = React.useState(() => {
    var _mockStores$activityN;
    const store = (_mockStores$activityN = _mockStores === null || _mockStores === void 0 ? void 0 : _mockStores.activityNotificationsStore) !== null && _mockStores$activityN !== void 0 ? _mockStores$activityN : new ActivityNotificationsStore(i18n);
    setGlobalActivityNotificationsStore(store);
    return store;
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [MobileHeader, setMobileHeader] = React.useState(null);
  const [floatingHeaderIsVisible, setFloatingHeaderIsVisible] = React.useState(false);
  const [mobileMaxWidth, setMobileMaxWidth] = React.useState(tokens['breakpoint-header-mobile-max']);
  const isMobile = (_useMatchMedia = useMatchMedia(`(max-width: ${mobileMaxWidth})`)) !== null && _useMatchMedia !== void 0 ? _useMatchMedia : false;
  const [headerStyles, setHeaderStyles] = React.useState(styles['header']);
  const [mobileHeaderStyles, setMobileHeaderStyles] = React.useState(styles['mobile-header']);
  const isAggregatorFeatureEnabled = headerStore.isAggregatorFeatureEnabled;
  const isLoadedTwoRowSearchExp = showTwoRowSearchBarMX !== SearchBarMXEnum.NotLoaded;
  const showTwoRowSearchBar = showTwoRowSearchBarMX === SearchBarMXEnum.NewBar;
  const {
    isSplitNavByCategoriesEnabled
  } = headerStore;
  const {
    Config,
    me
  } = udData;
  const {
    isMXUser
  } = getUserTypes({
    config: {
      features: Config.features,
      brand: Config.brand
    },
    me,
    headerData: headerStore.userSpecificContext
  });
  const showSplitCategoriesNav = isSplitNavByCategoriesEnabled && isMXUser;
  React.useEffect(() => {
    if (headerData.ufbContext && !headerStore.ufbContext) {
      headerStore.setUfbContext(headerData.ufbContext);
    }
  }, [headerData.ufbContext, headerStore]);
  React.useEffect(() => {
    async function bootstrap() {
      const isUFB = !!headerData.ufbContext;
      await loadCommonAppContext(locale, isUFB, response => {
        // TODO: rewire how `navigationCategories is resolved from `loadCommonAppContext()` response
        headerStore.setUserSpecificContext(response.data.header);
        activityNotificationsStore.setUserSpecificContext({
          user: headerStore.userSpecificContext.user,
          // If `nav_user_type = 'instructor'`, InstructorHeader is rendered.
          userType: null
        });
      });
      await browseNavStore.loadNavigationCategories(locale, isUFB);
      if (!headerStore.userSpecificContext.isLoggedIn) {
        browseNavStore.loadMostPopularTopicsForSubcategories();
      }
      // Fetch subscription info for non-organization users
      if (!isUFB) {
        await headerStore.fetchSubscriptionInfo();
      }
    }
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    const isUBUser = udData.me.is_authenticated && !!udData.me.organization;
    const isPPUser = headerStore.isPersonalPlanSubscriber;
    // Aggregator topics are loaded if the user is not a PP user, not a UB user, and the aggregator experiment is enabled.
    if (isAggregatorFeatureEnabled && !isUBUser && !isPPUser) {
      browseNavStore.loadNavigationAggregatorTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAggregatorFeatureEnabled, udData.me, headerStore.isPersonalPlanSubscriber]);
  React.useEffect(() => {
    async function loadMobileHeader() {
      if (isMobile) {
        await import('../mobile/mobile-header.react-component').then(headerModule => {
          setMobileHeader(headerModule.MobileHeader);
        });
      }
    }
    loadMobileHeader();
  }, [isMobile]);
  React.useEffect(() => {
    if (isAdminOrGroupAdmin) {
      setHeaderStyles(prevClassName => prevClassName + ' ' + styles['header-admin']);
      setMobileHeaderStyles(prevClassName => prevClassName + ' ' + styles['mobile-header-admin']);
      setMobileMaxWidth(tokens['breakpoint-md-max']);
    }
  }, [isAdminOrGroupAdmin]);
  function getSearchBarLabel() {
    var _headerStore$userSpec, _headerData$headerExp;
    if (isUFB && (_headerStore$userSpec = headerStore.userSpecificContext) !== null && _headerStore$userSpec !== void 0 && (_headerStore$userSpec = _headerStore$userSpec.user) !== null && _headerStore$userSpec !== void 0 && _headerStore$userSpec.enable_in_lecture_search_segments) {
      return i18n.gettext('What would you like to learn today?');
    }
    return ((_headerData$headerExp = headerData.headerExperimentFeatures) === null || _headerData$headerExp === void 0 ? void 0 : _headerData$headerExp.autocompleteLabel) || i18n.gettext('Search for anything');
  }
  function trackSeachBarSelected() {
    Tracker.publishEvent(new SearchBarSelected());
  }
  function handleHeaderIntersection(entry) {
    const intersectionRatio = entry.intersectionRatio;

    // If the header fully exits view, show the floating header.
    if (!floatingHeaderIsVisible && intersectionRatio === 0) {
      setFloatingHeaderIsVisible(true);
    }
    if (!isMobile && floatingHeaderIsVisible && intersectionRatio >= 0.5) {
      setFloatingHeaderIsVisible(false);
    }
  }
  function handleTopMarkIntersection(entry) {
    const {
      top
    } = entry.boundingClientRect;
    // However, only restore the non-floating header if we scroll back up to the top of the page.
    if (!isMobile && floatingHeaderIsVisible && top >= 0) {
      setFloatingHeaderIsVisible(false);
    }
  }
  function renderUserSpecificPart() {
    const {
      user
    } = headerStore.userSpecificContext;
    if (!user) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          flex: 1
        }
      });
    }
    if (!user.id) {
      return /*#__PURE__*/React.createElement(LoggedOutPart, {
        useLangPrefixedUrls: useLangPrefixedUrls,
        showSplitCategoriesNav: showSplitCategoriesNav
      });
    }
    return /*#__PURE__*/React.createElement(LoggedInPart, {
      useLangPrefixedUrls: useLangPrefixedUrls,
      showSplitCategoriesNav: showSplitCategoriesNav
    });
  }
  function renderHeaderBar(floating) {
    var _headerData$ufbContex, _headerData$ufbContex2;
    const showCobrandLogo = showCobrandingLogo({
      headerExperimentFeatures: headerData.headerExperimentFeatures,
      headerStore
    });
    return /*#__PURE__*/React.createElement("header", {
      className: classNames('ud-header', 'ud-text-sm', headerStyles, styles['flex-middle'], {
        [styles.floating]: floating
      }),
      "data-purpose": "header",
      "data-testid": "desktop-header"
    }, !floating && /*#__PURE__*/React.createElement(SkipToContentButton, {
      goToContentSelector: ".ud-main-content",
      label: i18n.gettext('Skip to content')
    }), /*#__PURE__*/React.createElement("a", {
      href: headerStore.urls.BROWSE,
      className: classNames(styles['flex-middle'], styles.logo)
    }, /*#__PURE__*/React.createElement(Logo, {
      ufbContext: headerData.ufbContext,
      width: showCobrandLogo ? 191 : 91,
      height: 34,
      showCobrandingLogo: showCobrandLogo
    })), (_headerData$ufbContex = (_headerData$ufbContex2 = headerData.ufbContext) === null || _headerData$ufbContex2 === void 0 ? void 0 : _headerData$ufbContex2.browseButtons) !== null && _headerData$ufbContex !== void 0 ? _headerData$ufbContex : /*#__PURE__*/React.createElement(BrowseNavDropdown, {
      className: styles['gap-button']
    }), /*#__PURE__*/React.createElement(SubscribeButton, {
      className: classNames(styles['gap-button'], styles['group-c'])
    }), isLoadedTwoRowSearchExp ? /*#__PURE__*/React.createElement(SearchFormAutocomplete, {
      searchFormAutocompleteStore: searchFormAutocompleteStore,
      formAction: headerStore.urls.SEARCH,
      formActionParams: headerStore.formActionParams,
      label: getSearchBarLabel(),
      inputProps: {
        name: 'q',
        onFocus: trackSeachBarSelected,
        className: 'js-header-search-field'
      },
      reversed: true,
      textSize: "small",
      className: styles['search-bar'],
      showResultsWithImage: true
    }) : /*#__PURE__*/React.createElement("div", {
      className: twoRowSearchStyles['no-search-bar']
    }), renderUserSpecificPart());
  }
  function renderTwoRowHeaderBar(floating) {
    var _headerData$ufbContex3, _headerData$ufbContex4;
    const showCobrandLogo = showCobrandingLogo({
      headerExperimentFeatures: headerData.headerExperimentFeatures,
      headerStore
    });
    return /*#__PURE__*/React.createElement("header", {
      className: classNames('ud-header', 'ud-text-sm', headerStyles, twoRowSearchStyles['multi-header'], {
        [styles.floating]: floating,
        'ud-header-shadow': floating
      }),
      "data-purpose": "header",
      "data-testid": "desktop-header-new"
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['flex-middle'])
    }, !floating && /*#__PURE__*/React.createElement(SkipToContentButton, {
      goToContentSelector: ".ud-main-content",
      label: i18n.gettext('Skip to content')
    }), /*#__PURE__*/React.createElement("a", {
      href: headerStore.urls.BROWSE,
      className: classNames(styles['flex-middle'], styles.logo)
    }, /*#__PURE__*/React.createElement(Logo, {
      ufbContext: headerData.ufbContext,
      width: showCobrandLogo ? 191 : 91,
      height: 34,
      showCobrandingLogo: showCobrandLogo
    })), (_headerData$ufbContex3 = (_headerData$ufbContex4 = headerData.ufbContext) === null || _headerData$ufbContex4 === void 0 ? void 0 : _headerData$ufbContex4.browseButtons) !== null && _headerData$ufbContex3 !== void 0 ? _headerData$ufbContex3 : /*#__PURE__*/React.createElement(BrowseNavDropdown, {
      className: styles['gap-button']
    }), /*#__PURE__*/React.createElement(SubscribeButton, {
      className: classNames(styles['gap-button'], styles['group-c'])
    }), /*#__PURE__*/React.createElement("div", {
      className: twoRowSearchStyles['no-search-bar']
    }), renderUserSpecificPart()), /*#__PURE__*/React.createElement("div", {
      className: classNames('container', styles['flex-middle'], twoRowSearchStyles['search-row'])
    }, isLoadedTwoRowSearchExp ? /*#__PURE__*/React.createElement(SearchFormAutocomplete, {
      submitButtonProps: {
        udStyle: 'primary',
        round: true,
        size: 'large'
      },
      searchFormAutocompleteStore: searchFormAutocompleteStore,
      formAction: headerStore.urls.SEARCH,
      formActionParams: headerStore.formActionParams,
      label: getSearchBarLabel(),
      inputProps: {
        name: 'q',
        onFocus: trackSeachBarSelected,
        className: 'js-header-search-field-multi'
      }
      // reversed={true}
      ,
      textSize: "small",
      className: twoRowSearchStyles['search-bar-lg'],
      formClassName: 'form-search-bar',
      showResultsWithImage: true
    }) : /*#__PURE__*/React.createElement(React.Fragment, null)));
  }
  function isShowFloatingHeader() {
    return floatingHeaderIsVisible && (persistentSearch || showTwoRowSearchBar);
  }
  function renderHeader(floating) {
    return showTwoRowSearchBar ? renderTwoRowHeaderBar(floating) : renderHeaderBar(floating);
  }
  function renderFloatingHeader() {
    return renderHeader(true);
  }
  function renderNonFloatingHeader() {
    return /*#__PURE__*/React.createElement(Observer, {
      onChange: handleHeaderIntersection
    }, renderHeader(false));
  }
  const headerProviderContext = {
    headerStore: headerStore,
    browseNavStore: browseNavStore,
    myLearningStore: myLearningStore,
    activityNotificationsStore: activityNotificationsStore,
    ufbContext: headerData.ufbContext,
    headerExperimentFeatures: headerData.headerExperimentFeatures
  };
  const isUbContext = !!headerData.ufbContext && Object.keys(headerData.ufbContext).length > 0;
  const isTeamPlan = (_udData$Config = udData.Config) === null || _udData$Config === void 0 || (_udData$Config = _udData$Config.brand) === null || _udData$Config === void 0 ? void 0 : _udData$Config.is_team;

  // Check if smart bar is enabled via feature flag
  // This prevents unnecessary component mounting when disabled (e.g., on cart page)
  const isSmartBarEnabled = (_udData$Config2 = udData.Config) === null || _udData$Config2 === void 0 || (_udData$Config2 = _udData$Config2.features) === null || _udData$Config2 === void 0 || (_udData$Config2 = _udData$Config2.notice) === null || _udData$Config2 === void 0 ? void 0 : _udData$Config2.smart_bar;
  return /*#__PURE__*/React.createElement(HeaderContextProvider, headerProviderContext, showSmartBar && isSmartBarEnabled && /*#__PURE__*/React.createElement(SmartBarFixedHeight, {
    isPersonalPlanSubscriber: headerStore.isPersonalPlanSubscriber,
    isUdemyBusinessSubscriber: isUbContext && !isTeamPlan,
    isEnterprisePlanSubscriber: isUbContext && !isTeamPlan,
    isTeamPlanSubscriber: isUbContext && isTeamPlan,
    isUserContextReady: !!headerStore.userSpecificContext.user
  }), showSmartBar && isSmartBarEnabled && /*#__PURE__*/React.createElement(SmartBarFixedHeightSpacer, {
    disableHideButtonWhenVisible: true
  }), ((_headerStore$userSpec2 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec2 === void 0 ? void 0 : _headerStore$userSpec2.id) && (!MobileHeader || !isMobile) && /*#__PURE__*/React.createElement(LabsLearningBanner, null), /*#__PURE__*/React.createElement("div", {
    className: mobileHeaderStyles
  }, MobileHeader ? (/*#__PURE__*/React.createElement(MobileHeader, {
    ...headerData,
    ufbContext: (_headerData$ufbContex5 = headerData.ufbContext) === null || _headerData$ufbContex5 === void 0 ? void 0 : _headerData$ufbContex5.mobileContext,
    isInsideDesktopHeader: true,
    useLangPrefixedUrls,
    showTwoRowSearchBarMX
  })) : /*#__PURE__*/React.createElement("div", {
    "data-testid": "mobile-header-placeholder",
    className: styles['mobile-header-placeholder']
  })), /*#__PURE__*/React.createElement(Observer, {
    onChange: handleTopMarkIntersection
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.mark,
    "data-testid": "desktop-top-mark"
  })), renderNonFloatingHeader(), isShowFloatingHeader() && renderFloatingHeader(), !headerStore.userSpecificContext.isLoggedIn && /*#__PURE__*/React.createElement("div", {
    "data-testid": "seo-links"
  }, /*#__PURE__*/React.createElement(SeoLinks, {
    navigationCategories: browseNavStore.navigationCategories,
    mostPopularSubcategories: browseNavStore.mostPopularSubcategories
  })));
});
//# sourceMappingURL=desktop-header.react-component.js.map